import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { updateBp } from 'services/BpService'
import { ink, line, radius, font, surface, transition } from 'features/dashboard/tokens'

const MAX_LENGTH = 255

const BuildingOwnerField = ({ bpId, value, canEdit = false, onSaved }) => {
  const [owner, setOwner] = useState(value || '')
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const inputRef = useRef(null)

  useEffect(() => {
    setOwner(value || '')
    setDraft(value || '')
    setEditing(false)
    setError(null)
  }, [bpId, value])

  useEffect(() => {
    if (editing) inputRef.current?.focus()
  }, [editing])

  const startEditing = () => {
    setDraft(owner)
    setError(null)
    setEditing(true)
  }

  const cancel = () => {
    setDraft(owner)
    setError(null)
    setEditing(false)
  }

  const save = async () => {
    const trimmed = draft.trim()
    if (trimmed.length > MAX_LENGTH) {
      setError(`Please keep this under ${MAX_LENGTH} characters.`)
      return
    }
    if (trimmed === owner) {
      setEditing(false)
      return
    }
    setSaving(true)
    setError(null)
    try {
      await updateBp({ id: bpId, buildingOwner: trimmed || null })
      setOwner(trimmed)
      setEditing(false)
      toast.success(trimmed ? 'Building owner saved.' : 'Building owner removed.')
      onSaved && onSaved(trimmed || null)
    } catch (err) {
      setError(err?.response?.data?.errors?.building_owner?.[0] || err?.response?.data?.message || 'We couldn’t save the building owner. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault()
      save()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancel()
    }
  }

  return (
    <Wrap>
      {editing ? (
        <>
          <Label as='label' htmlFor={`building-owner-input-${bpId}`}>Building owner</Label>
          <Input
            id={`building-owner-input-${bpId}`}
            ref={inputRef}
            value={draft}
            maxLength={MAX_LENGTH}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder='e.g. Harbour Property SPV Ltd'
            aria-invalid={!!error}
            aria-describedby={`building-owner-help-${bpId}`}
            disabled={saving}
          />
          <Help id={`building-owner-help-${bpId}`}>The company, SPV or person who legally owns this building. It is shown on your Passports list and does not give anyone access.</Help>
          {error && <ErrorText role='alert'>{error}</ErrorText>}
          <Actions>
            <SaveButton type='button' onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</SaveButton>
            <CancelButton type='button' onClick={cancel} disabled={saving}>Cancel</CancelButton>
          </Actions>
        </>
      ) : (
        <>
          <Row>
            <Label>Building owner</Label>
            <Value $empty={!owner} title={owner || 'No building owner recorded yet'}>{owner || 'Not set'}</Value>
          </Row>
          {canEdit && (
            <Actions $end>
              <EditButton type='button' onClick={startEditing} aria-label={owner ? 'Edit the building owner' : 'Add the building owner'}>
                {owner ? <EditRoundedIcon aria-hidden='true' /> : <AddRoundedIcon aria-hidden='true' />}
                {owner ? 'Edit' : 'Add building owner'}
              </EditButton>
            </Actions>
          )}
        </>
      )}
    </Wrap>
  )
}

export default BuildingOwnerField

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 6px 0;
  border-bottom: 1px solid ${surface.wash};
`

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
`

const Label = styled.span`
  font-size: ${font.caption};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-weight: 700;
`

const Value = styled.span`
  font-size: ${font.body};
  font-weight: ${({ $empty }) => ($empty ? 400 : 600)};
  color: ${({ $empty, theme }) => ($empty ? '#6b7280' : theme.colors.haiti)};
  text-align: right;
  max-width: 60%;
  overflow-wrap: anywhere;
`

const EditButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 40px;
  padding: 0 10px;
  border: 1px solid ${line.base};
  border-radius: ${radius.sm};
  background: #fff;
  color: ${ink.base};
  font: inherit;
  font-size: ${font.small};
  font-weight: 600;
  cursor: pointer;
  transition: ${transition};
  svg { font-size: 16px; }
  &:hover { background: #f4f4f5; }
  &:focus-visible { outline: 2px solid ${ink.base}; outline-offset: 2px; }
`

const Input = styled.input`
  width: 100%;
  min-height: 44px;
  padding: 0 12px;
  border: 1px solid ${line.base};
  border-radius: ${radius.sm};
  font: inherit;
  font-size: 16px;
  color: ${ink.base};
  box-sizing: border-box;
  &:focus { outline: 2px solid ${({ theme }) => theme.colors.secondary}; outline-offset: 0; border-color: transparent; }
  &[aria-invalid='true'] { border-color: #b00020; }
`

const Help = styled.p`
  margin: 0;
  font-size: ${font.small};
  color: ${ink.mute};
  line-height: 1.5;
`

const ErrorText = styled.p`
  margin: 0;
  font-size: ${font.small};
  color: #b00020;
`

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: ${({ $end }) => ($end ? 'flex-end' : 'flex-start')};
`

const SaveButton = styled.button`
  min-height: 40px;
  padding: 0 16px;
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  border-radius: ${radius.sm};
  background: ${({ theme }) => theme.colors.secondary};
  color: #fff;
  font: inherit;
  font-size: ${font.body};
  font-weight: 700;
  cursor: pointer;
  &:disabled { opacity: 0.6; cursor: default; }
  &:focus-visible { outline: 2px solid ${ink.base}; outline-offset: 2px; }
`

const CancelButton = styled.button`
  min-height: 40px;
  padding: 0 16px;
  border: 1px solid ${line.base};
  border-radius: ${radius.sm};
  background: #fff;
  color: ${ink.base};
  font: inherit;
  font-size: ${font.body};
  font-weight: 600;
  cursor: pointer;
  &:disabled { opacity: 0.6; cursor: default; }
  &:focus-visible { outline: 2px solid ${ink.base}; outline-offset: 2px; }
`
